import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
// import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatDate } from "../utils/dateUtils"; // adjust path
import {
  Box,
  MenuItem,
  Pagination,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  TableContainer,
  CssBaseline,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Popover,
  Avatar,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import debounce from "lodash.debounce";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import FilterList from "@mui/icons-material/FilterList";
import Tooltip from "@mui/material/Tooltip";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Security configuration
const SECURITY_CONFIG = {
  TIME_MANIPULATION_THRESHOLD: 5000, // 5 seconds - adjust as needed
  LOGOUT_DELAY: 5000, // 5 seconds delay to show warning message
  ENABLE_TIME_PROTECTION: true, // Can be disabled for testing
};

// Always convert any date to IST
const toIST = (date = new Date()) => {
  return new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
};

const getISTDateString = (date = new Date()) => {
  const d = toIST(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// IST datetime formatter
const getISTDateTimeString = (date = new Date()) => {
  const d = toIST(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  const hour = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${min}:00`;
};

// Parse "YYYY-MM-DD HH:MM:SS" into Date object in local time
const getISTDateObj = () => {
  const istString = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });
  return new Date(istString);
};

const getTodayDate = () => {
  const d = getISTDateObj();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getYesterdayDate = () => {
  const d = getISTDateObj();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTwoDaysAgoDate = () => {
  const d = getISTDateObj();
  d.setDate(d.getDate() - 2);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const formatToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}/${year}`;
};

const EmployeeAttendance = () => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const navigate = useNavigate();
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [dateError, setDateError] = useState(""); // To show inline error
  const [todayHistory, setTodayHistory] = useState([]);
  const [weeklyHistory, setWeeklyHistory] = useState([]); // Full week data for bar chart
  const [profilePicture, setProfilePicture] = useState(null); // Employee profile picture
  const [empName, setEmpName] = useState({});
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [actionType, setActionType] = useState("checkin");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [searchEmpName, setSearchEmpName] = useState("");
  const [searchDateFrom, setSearchDateFrom] = useState("");
  const [searchDateTo, setSearchDateTo] = useState("");
  const [filters, setFilters] = useState({});
  const [anchorEls, setAnchorEls] = useState({});
  const [comments, setComments] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [checkInDisabled, setCheckInDisabled] = useState(false);
  const [checkOutDisabled, setCheckOutDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [checkInTime, setCheckInTime] = useState({
    hour: "",
    min: "",
    meridian: "AM",
  });
  const [checkOutTime, setCheckOutTime] = useState({
    hour: "",
    min: "",
    meridian: "AM",
  });
  //const [date, setDate] = useState(() => getLocalDateString());
  // const [date, setDate] = useState(() => getISTDateString());
  const [displayDate, setDisplayDate] = useState(
    formatToMMDDYYYY(getISTDateString()),
  );
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0); // For main table
  const [historyTotalPages, setHistoryTotalPages] = useState(0); // For history dialog

  // Debug: Log when totalPages changes
  useEffect(() => {
    console.log("historyTotalPages state changed to:", historyTotalPages);
  }, [historyTotalPages]);
  const [searchDate, setSearchDate] = useState(getTodayDate());
  const [checkOutDate, setCheckOutDate] = useState(getISTDateString());
  const [pendingRecord, setPendingRecord] = useState(null); // Track pending check-in record
  const [datesCrossMidnight, setDatesCrossMidnight] = useState(false); // Track if checkout crosses midnight
  const [currentDateTime, setCurrentDateTime] = useState(new Date()); // Real-time date/time
  const [lastKnownTime, setLastKnownTime] = useState(Date.now()); // For detecting time manipulation
  const userRole = Array.isArray(roles) ? roles[0] : roles;

  // Tracks whether the user has manually edited the date/time fields.
  // When true, fetchTodayHistory will not overwrite the user's selections.
  const userIsEditing = useRef(false);
  // Granular touched refs — each guards its own field group in fetchTodayHistory
  const userTouchedCheckIn = useRef(false); // Hr/Min/AM-PM for check-in
  const userTouchedCheckOut = useRef(false); // Hr/Min/AM-PM for check-out
  const userTouchedCheckOutDate = useRef(false); // checkout date picker

  // Fetch history when page or limit changes (only when dialog is open)
  useEffect(() => {
    if (historyOpen && empId) {
      // Call with proper parameters: dateOrNull, empId, userRole, empName, dateFrom, dateTo, page
      fetchHistoryByDate(
        null,
        empId,
        userRole,
        searchEmpName,
        searchDateFrom,
        searchDateTo,
        page + 1,
      );
    }
  }, [
    page,
    limit,
    filters,
    historyOpen,
    empId,
    userRole,
    searchEmpName,
    searchDateFrom,
    searchDateTo,
  ]);

  const handleLogout = async () => {
    try {
      // IMPORTANT: Wait for logout API to complete (clears HttpOnly cookie)
      await fetch(`${BASE_URL}/api/logout`, {
        method: "POST",
        credentials: "include", // Important for CORS with cookies
      });
    } catch (error) {
      console.error("Logout API failed:", error);
      // Continue with logout even if API fails
    } finally {
      // Clear session storage only (cookies cleared by backend)
      try {
        sessionStorage.clear();
      } catch (error) {
        console.error("Storage clear error:", error);
      }

      // Navigate after API call completes
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    if (!empId || !boId) return;

    const fetchLoginUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/checkin/${empId}/${boId}`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const data = await res.json();
        if (data.length > 0) {
          setEmpName(data[0]); // store first object instead of whole array
        }

        // Fetch profile picture
        try {
          const picRes = await fetch(
            `${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`,
          );
          if (picRes.ok) {
            const picData = await picRes.json();
            if (picData.success && picData.data?.profile_picture_url) {
              setProfilePicture(picData.data.profile_picture_url);
            }
          }
        } catch (picErr) {
          console.error("Failed to fetch profile picture:", picErr);
        }
        await fetchTodayHistory(page, empId, boId);
        await fetchLastStatus();

        // Additional immediate validation
        setTimeout(async () => {
          const immediateCheck = await getLastPendingCheckIn(empId);

          if (immediateCheck?.pending) {
            const pendingDate = immediateCheck.attendance_date;
            const today = getTodayDate();

            if (pendingDate !== today) {
              setCheckInDisabled(true);
              setCheckOutDisabled(false);
              setIsCheckedIn(true);
              setActionType("checkout");

              const formattedDate = formatToMMDDYYYY(pendingDate);
              setStatusMsg(
                `⚠️ CRITICAL: Pending checkout from ${formattedDate} detected. Complete checkout to enable new check-ins.`,
              );
              setMessage(
                `System detected pending checkout from ${formattedDate}. Please complete that checkout first.`,
              );
              setOpen(true);
            }
          }
        }, 2000); // Check again after 2 seconds
      } catch (err) {
        console.error("Failed to fetch employee details", err);
      }
    };
    fetchLoginUser();

    // Listen for profile picture changes broadcast from Profile page
    const channel = new BroadcastChannel("profile_picture");
    channel.onmessage = (e) => {
      if (e.data.type === "updated") {
        setProfilePicture(e.data.url);
      } else if (e.data.type === "deleted") {
        setProfilePicture(null);
      }
    };
    return () => channel.close();
  }, [empId, boId, page]);

  useEffect(() => {
    fetchLastStatus();
    fetchTodayHistory(page, empId, boId);
    fetchWeeklyHistory();
  }, [page, limit, empId, boId]);

  // Real-time date/time updates with time manipulation detection
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const currentTime = now.getTime();
      const expectedTime = lastKnownTime + 1000; // Expected time (1 second later)
      const timeDifference = Math.abs(currentTime - expectedTime);

      // Detect significant time jumps (configurable threshold)
      // This indicates system time was manually changed
      if (
        SECURITY_CONFIG.ENABLE_TIME_PROTECTION &&
        lastKnownTime > 0 &&
        timeDifference > SECURITY_CONFIG.TIME_MANIPULATION_THRESHOLD
      ) {
        console.warn("System time manipulation detected!", {
          lastKnownTime: new Date(lastKnownTime).toISOString(),
          currentTime: new Date(currentTime).toISOString(),
          difference: timeDifference / 1000 + " seconds",
        });

        // Force logout due to time manipulation
        handleTimeManipulationDetected(timeDifference);
        return;
      }

      const previousDate = getISTDateString(currentDateTime);
      const newDate = getISTDateString(now);

      setCurrentDateTime(now); // store real UTC-based Date, format as IST only at display time
      setLastKnownTime(currentTime);

      // Check if day has changed naturally (midnight crossed)
      if (
        previousDate !== newDate &&
        currentDateTime.getTime() > 0 &&
        timeDifference <= SECURITY_CONFIG.TIME_MANIPULATION_THRESHOLD
      ) {
        // Update checkout date to new day if user is checked in
        if (isCheckedIn) {
          const newDateISO = getISTDateString(now);
          setCheckOutDate(newDateISO);
          setDatesCrossMidnight(true);

          // Show notification about day change
          setMessage(
            `Day changed to ${formatToMMDDYYYY(
              newDateISO,
            )}. Checkout date updated automatically.`,
          );
          setOpen(true);
        }
        fetchTodayHistory(page, empId, boId);
        fetchLastStatus();
      }
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, [currentDateTime, isCheckedIn, page, empId, boId, lastKnownTime]);

  // Handle time manipulation detection
  const handleTimeManipulationDetected = (timeDifference) => {
    const minutes = Math.round(timeDifference / 1000 / 60);
    const direction = timeDifference > 0 ? "forward" : "backward";

    // Clear all sensitive data
    setIsCheckedIn(false);
    setCheckInDisabled(false);
    setCheckOutDisabled(true);
    setPendingRecord(null);
    setTodayHistory([]);

    // Show security warning
    setMessage(
      `Security Alert: System time was changed ${direction} by ${minutes} minutes. ` +
        `For security reasons, you have been logged out. Redirecting to login...`,
    );
    setOpen(true);

    // Force logout after a short delay to show the message
    setTimeout(() => {
      // Clear session data only
      sessionStorage.clear();

      // Use the proper logout function
      handleLogout();
    }, SECURITY_CONFIG.LOGOUT_DELAY);
  };

  // set rows per page
  const rowsPerPage = 10;

  // Real-time current date and time in IST
  const currentDate = currentDateTime.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const currentTime = currentDateTime.toLocaleTimeString("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  // handle history dialog close
  const handleHistoryClose = () => {
    setHistoryOpen(false);
    setSearchEmpName("");
    setSearchDate(getTodayDate());
    setSearchDateFrom("");
    setSearchDateTo("");
    setHistoryData([]);
    setFilters({});
    setAnchorEls({});
    setPage(0); // Reset page when closing
    setLimit(10); // Reset limit when closing
  };

  const handleFilterIconClick = (field, event) => {
    setAnchorEls((prev) => ({ ...prev, [field]: event.currentTarget }));
  };

  const handleFilterClose = (field) => {
    setAnchorEls((prev) => ({ ...prev, [field]: null }));
  };
  const hours = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0"),
  );
  // const minutes = ["00", "15", "30", "45"];
  // full minutes list
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );
  // AM/PM options
  const ampm = ["AM", "PM"];

  // Update current time every second
  function parseLocalDateTime(dateTimeStr) {
    const [datePart, timePart] = dateTimeStr.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, min, sec = 0] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, min, sec); // local time
  }
  // Get today's date in MM/DD/YYYY format for comparison
  const dateIST = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });

  const d = new Date(dateIST);

  const formattedCurrentDate =
    `${String(d.getMonth() + 1).padStart(2, "0")}/` +
    `${String(d.getDate()).padStart(2, "0")}/` +
    `${d.getFullYear()}`;

  const createISTDateFromString = (str) => {
    const [datePart, timePart] = str.split(" ");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm, ss] = timePart.split(":").map(Number);

    // Just create Date in local time (no UTC shift)
    return new Date(y, m - 1, d, hh, mm, ss);
  };

  // Create an IST date from Y-M-D and H:M in IST
  const createISTDate = (y, m, d, hh, mm) => {
    // Convert IST → UTC by subtracting 5:30
    return new Date(Date.UTC(y, m - 1, d, hh - 5, mm - 30, 0));
  };
  const getISTTimestamp = (dateStr, timeObj, dateOnly = false) => {
    const [y, m, d] = dateStr.split("-").map(Number);

    let hh, mm;

    if (!timeObj) {
      const nowIST = toIST();
      hh = nowIST.getHours();
      mm = nowIST.getMinutes();
    } else {
      hh = parseInt(timeObj.hour, 10);
      mm = parseInt(timeObj.min || 0, 10);

      if (timeObj.meridian === "PM" && hh !== 12) hh += 12;
      if (timeObj.meridian === "AM" && hh === 12) hh = 0;
    }

    const finalDate = createISTDate(y, m, d, hh, mm);

    if (dateOnly) return getISTDateString(finalDate);

    return getISTDateTimeString(finalDate);
  };

  const handleCheckIn = async () => {
    setValidationError("");

    // IMMEDIATE HARD-STOP: Check database directly for any pending checkouts
    try {
      const directCheckResponse = await fetch(
        `${BASE_URL}/api/checkin/today-history?empId=${empId}&boId=${boId}&currentDate=${getISTDateString(
          currentDateTime,
        )}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (directCheckResponse.ok) {
        const historyData = await directCheckResponse.json();
        // Check if any record has checkin_time but no checkout_time
        const pendingRecords =
          historyData.data?.filter((record) => {
            const hasCheckin =
              record.checkin_time && record.checkin_time !== "—";
            const hasCheckout =
              record.checkout_time && record.checkout_time !== "—";
            return hasCheckin && !hasCheckout;
          }) || [];

        if (pendingRecords.length > 0) {
          const latestPending = pendingRecords[0];
          const pendingDate = latestPending.attendance_date;
          const today = getTodayDate();
          const todayFormatted = formatToMMDDYYYY(today);

          // If pending date is not today, block check-in
          if (pendingDate !== todayFormatted) {
            const errorMessage = `SYSTEM BLOCK: You have a pending checkout from ${pendingDate}. Complete that checkout before checking in again.`;

            setValidationError(errorMessage);
            setMessage(errorMessage);
            setOpen(true);

            // Force checkout mode
            setCheckInDisabled(true);
            setCheckOutDisabled(false);
            setIsCheckedIn(true);
            setActionType("checkout");
            return; // BLOCK CHECK-IN
          }
        }
      }
    } catch (directCheckError) {
      console.error("Direct check failed:", directCheckError);
      // Continue with original validation if direct check fails
    }

    // ORIGINAL VALIDATION (as backup)
    const pendingRecord = await getLastPendingCheckIn(empId);
    if (pendingRecord?.pending) {
      const pendingDate = pendingRecord.attendance_date;
      const today = getTodayDate();
      if (pendingDate !== today) {
        // There's a pending checkout from a previous day
        const formattedPendingDate = formatToMMDDYYYY(pendingDate);
        const errorMessage = `You have a pending checkout from ${formattedPendingDate}. Please complete that checkout before checking in again.`;

        console.log("BLOCKING CHECK-IN:", errorMessage);

        setValidationError(errorMessage);
        setMessage(
          `Pending checkout detected for ${formattedPendingDate}. Please check out for that day first.`,
        );
        setOpen(true);

        // Force the UI to show checkout mode for the pending record
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true);
        setActionType("checkout");

        return; // Prevent check-in
      }
    }

    // Additional safety check - verify no records exist without checkout for this employee
    try {
      const verificationResponse = await fetch(
        `${BASE_URL}/api/checkin/verify-no-pending/${empId}/${boId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (verificationResponse.ok) {
        const verificationData = await verificationResponse.json();
        if (verificationData.hasPending) {
          setValidationError(
            "System detected a pending checkout. Please refresh the page and complete your previous checkout first.",
          );
          return;
        }
      }
    } catch (verificationError) {
      console.warn(
        "Verification check failed (continuing):",
        verificationError,
      );
      // Continue with check-in if verification endpoint doesn't exist yet
    }
    const name = empName.employee_name;

    // Use the selected date from displayDate (convert MM/DD/YYYY to YYYY-MM-DD)
    let attendance_date;
    if (displayDate && displayDate.includes("/")) {
      const [month, day, year] = displayDate.split("/");
      attendance_date = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0",
      )}`;
    } else {
      attendance_date = getISTDateString(); // fallback to today
    }

    // If user hasn't selected time, default to current IST time
    let checkinTimeToUse = checkInTime;
    if (!checkInTime.hour || !checkInTime.min || !checkInTime.meridian) {
      const nowIST = toIST(); // your helper that returns current IST as Date
      let hours = nowIST.getHours();
      const minutes = nowIST.getMinutes();
      const meridian = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12; // convert 24h -> 12h

      checkinTimeToUse = {
        hour: hours.toString().padStart(2, "0"),
        min: minutes.toString().padStart(2, "0"),
        meridian,
      };

      setCheckInTime(checkinTimeToUse); // optional, update UI
    }

    // Always get IST datetime
    const checkin_time = getISTTimestamp(attendance_date, checkinTimeToUse);

    // Validation
    const checkinDateObj = createISTDateFromString(checkin_time);
    const nowIST = toIST();

    // Validate check-in date is not in the future
    if (checkinDateObj > nowIST) {
      setValidationError("Check-in time cannot be in the future.");
      return;
    }

    // Validate check-in date is not more than 2 days old (except for missed checkouts)
    const twoDaysAgo = new Date(nowIST);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    twoDaysAgo.setHours(0, 0, 0, 0); // Start of 2 days ago

    if (checkinDateObj < twoDaysAgo) {
      setValidationError(
        "Check-in date cannot be more than 2 days old. Please contact administrator for older entries.",
      );
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/checkin/in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          boId,
          empId,
          name,
          attendance_date,
          checkin_time,
          checkin_comments: comments,
        }),
      });

      const data = await response.json();
      const success = response.ok; // treat any 2xx as success

      if (success) {
        userIsEditing.current = false; // Allow fetchTodayHistory to refresh state after check-in
        userTouchedCheckIn.current = false;
        userTouchedCheckOut.current = false;
        userTouchedCheckOutDate.current = false;
        setIsCheckedIn(true);
        setComments("");
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setActionType("checkout");
      } else {
        setIsCheckedIn(false);
        setCheckInDisabled(false);
        setCheckOutDisabled(true);
      }

      const fallback = success ? "Check-in successful" : "Check-in failed";
      setMessage((data && data.message) || fallback);
      setOpen(true);

      // Small delay to ensure backend has committed the transaction
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Refresh data and status and wait for them so UI reflects new state
      try {
        await fetchTodayHistory(page, empId, boId);
        await fetchLastStatus();
        fetchWeeklyHistory();
      } catch (refreshErr) {
        console.error("Error refreshing after check-in:", refreshErr);
      }
    } catch (error) {
      console.error("Check-in error:", error);
      setMessage("Check-in error");
      setOpen(true);
      setIsCheckedIn(true);
      fetchTodayHistory(page, empId, boId);
    }
  };

  const handleCheckOut = async () => {
    setValidationError("");

    const name = empName.employee_name;
    const checkout_comments = comments;

    const record = await getLastPendingCheckIn(empId);
    if (!record?.pending) {
      setMessage("No pending check-in found for checkout.");
      setOpen(true);
      return;
    }

    // -----------------------------
    // 1️⃣ Resolve checkout time
    // -----------------------------
    let checkoutTimeToUse = checkOutTime;

    if (!checkoutTimeToUse.hour || !checkoutTimeToUse.min) {
      const nowIST = toIST();
      let h = nowIST.getHours();
      const m = nowIST.getMinutes();
      const meridian = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;

      checkoutTimeToUse = {
        hour: String(h).padStart(2, "0"),
        min: String(m).padStart(2, "0"),
        meridian,
      };

      setCheckOutTime(checkoutTimeToUse);
    }

    const { attendance_id, attendance_date, checkin_time } = record;

    // -----------------------------
    // 2️⃣ Build Date Objects (NO AUTO SHIFT)
    // -----------------------------
    const checkout_time = getISTTimestamp(checkOutDate, checkoutTimeToUse);

    const checkinDateObj = createISTDateFromString(checkin_time);
    const checkoutDateObj = createISTDateFromString(checkout_time);
    const nowIST = toIST();

    // -----------------------------
    // 3️⃣ Validation Rules
    // -----------------------------

    // RULE A — Same day → checkout must be AFTER check-in
    if (checkOutDate === attendance_date) {
      if (checkoutDateObj <= checkinDateObj) {
        setValidationError(
          "Check-out time must be after check-in time for the same day.",
        );
        return;
      }
    }

    // RULE B — Allow ONLY next day for midnight crossing
    const checkinDay = new Date(attendance_date);
    const checkoutDay = new Date(checkOutDate);

    checkinDay.setHours(0, 0, 0, 0);
    checkoutDay.setHours(0, 0, 0, 0);

    const diffDays =
      (checkoutDay.getTime() - checkinDay.getTime()) / (1000 * 60 * 60 * 24);

    if (diffDays < 0 || diffDays > 1) {
      setValidationError(
        "Checkout date must be the same day or the next day after check-in.",
      );
      return;
    }

    // RULE C — No future checkout
    if (checkoutDateObj > nowIST) {
      setValidationError("Check-out time cannot be in the future.");
      return;
    }

    // RULE D — Max 24 hours
    const workingHours = (checkoutDateObj - checkinDateObj) / (1000 * 60 * 60);

    if (workingHours <= 0) {
      setValidationError("Invalid checkout time.");
      return;
    }

    if (workingHours > 24) {
      setValidationError(
        "Working hours cannot exceed 24 hours. Please verify your dates.",
      );
      return;
    }

    // -----------------------------
    // 4️⃣ Submit Checkout
    // -----------------------------
    try {
      const response = await fetch(`${BASE_URL}/api/checkin/out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          empId,
          boId,
          name,
          attendance_id,
          attendance_date,
          checkout_time,
          checkout_comments,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        userIsEditing.current = false; // Allow fetchTodayHistory to refresh state after check-out
        userTouchedCheckIn.current = false;
        userTouchedCheckOut.current = false;
        userTouchedCheckOutDate.current = false;
        setIsCheckedIn(false);
        setComments("");
        setDatesCrossMidnight(diffDays === 1); // only user-selected midnight
      }

      setMessage(data.message || "Checkout successful");
      setOpen(true);

      // Small delay to ensure backend has committed the transaction
      await new Promise((resolve) => setTimeout(resolve, 100));

      await fetchTodayHistory(page, empId, boId);
      await fetchLastStatus();
      fetchWeeklyHistory();
    } catch (error) {
      console.error("Check-out error:", error);
      setMessage("Check-out failed. Please try again.");
      setOpen(true);
    }
  };

  const getLastPendingCheckIn = async (empId) => {
    try {
      // CORRECTED: Don't send currentDate - we want ANY pending checkout regardless of date
      const res = await fetch(
        `${BASE_URL}/api/checkin/last-pending/${empId}/${boId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );

      const result = await res.json();
      // Additional validation - ensure we have proper data structure
      if (result && typeof result === "object") {
        // If there's an attendance_id but no checkout_time, it's pending
        if (result.attendance_id && !result.checkout_time) {
          result.pending = true;
        }
      }

      return result;
    } catch (error) {
      console.error("Error fetching last pending check-in:", error);
      return null;
    }
  };

  const fetchLastStatus = async () => {
    setIsStatusLoading(true);

    const record = await getLastPendingCheckIn(empId);
    setPendingRecord(record); // Store the pending record in state
    const today = getTodayDate();

    if (record?.pending) {
      const pendingDate = record.attendance_date;

      if (pendingDate === today) {
        // Today's check-in exists but not checked out
        console.log("Today's check-in found, enabling checkout");
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true); // Set isCheckedIn to true for today
        setActionType("checkout");
        setStatusMsg(
          `You are currently checked in for today (${formatToMMDDYYYY(
            today,
          )}). Please check out when you're done.`,
        );
      } else {
        // Previous day's check-in is pending - BLOCK new check-ins
        const formattedDate = formatToMMDDYYYY(pendingDate);
        const warningMessage = `PENDING CHECKOUT: You forgot to check out on ${formattedDate}. You must complete that checkout before checking in again.`;

        setMessage(warningMessage);
        setOpen(true);

        // CRITICAL: Disable check-in until pending checkout is resolved
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true); // Keep isCheckedIn true for pending check-in
        setActionType("checkout");
        setStatusMsg(
          `Pending checkout required for ${formattedDate}. Complete checkout to enable new check-ins.`,
        );

        console.warn("Pending checkout detected:", {
          pendingDate,
          today,
          message: warningMessage,
        });
      }
    } else {
      // No pending check-in, allow check-in
      console.log("No pending checkout, enabling check-in");
      setCheckInDisabled(false);
      setCheckOutDisabled(true);
      setIsCheckedIn(false); // Ensure isCheckedIn is false when no pending check-in
      setActionType("checkin");
      // Clear any previous status message when there is no pending record
      setStatusMsg("");
    }

    setIsStatusLoading(false);
  };

  const padZero = (num) => (num < 10 ? `0${num}` : `${num}`);
  const parseTimeTo12Hour = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") {
      return { hour: "", min: "", meridian: "AM" };
    }

    let raw = timeStr.trim().toUpperCase();

    // CASE 1 — format "02:00 PM"
    if (raw.includes("AM") || raw.includes("PM")) {
      const [hm, mer] = raw.split(" ");
      const [h, m] = hm.split(":");

      return {
        hour: h?.padStart(2, "0") || "",
        min: m?.padStart(2, "0") || "",
        meridian: mer || "AM",
      };
    }

    // CASE 2 — format "14:25:00" or "14:25"
    const parts = raw.split(":");
    const hour24 = parseInt(parts[0], 10);

    if (isNaN(hour24)) return { hour: "", min: "", meridian: "AM" };

    const min = parts[1]?.substring(0, 2) || "00";

    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const meridian = hour24 >= 12 ? "PM" : "AM";

    return {
      hour: String(hour12).padStart(2, "0"),
      min: String(min).padStart(2, "0"),
      meridian,
    };
  };

  const fetchTodayHistory = async (pageNum = 1, empId, boId) => {
    try {
      // Reset midnight crossing flag when fetching new data
      setDatesCrossMidnight(false);

      // Send frontend's current date to backend for consistency
      const frontendCurrentDate = getISTDateString(currentDateTime);

      const queryParams = new URLSearchParams({
        page: pageNum,
        limit,
        empId,
        boId,
        currentDate: frontendCurrentDate, // Send frontend date to backend
      });
      const res = await fetch(
        `${BASE_URL}/api/checkin/today-history?${queryParams}`,
      );
      const data = await res.json();

      const rows = data?.data || [];
      // Format attendance_date as MM/DD/YYYY for display & matching
      const formattedRows = rows.map((item) => ({
        ...item,
        attendance_date: formatToMMDDYYYY(item.attendance_date), // e.g. "11/25/2025"
        checkin_time: item.checkin_time || "",
        checkout_time: item.checkout_time || "",
      }));

      setTodayHistory(formattedRows);
      setTotalPages(data.pagination.totalPages);
      // Don't set page from response - frontend controls page state

      // Fetch last pending checkin
      const record = await getLastPendingCheckIn(empId);

      // If the user is actively editing the date/time fields, don't overwrite their selections.
      if (userIsEditing.current) return;

      if (record?.pending) {
        // Pending record case
        const pendingDateDisplay = formatToMMDDYYYY(record.attendance_date); // For UI display
        setDisplayDate(pendingDateDisplay);

        // For checkout date, use today's date as default, but this will be corrected during checkout if needed
        const todayISO = getISTDateString(); // e.g. "2025-11-25"
        if (!userTouchedCheckOutDate.current) setCheckOutDate(todayISO);

        // Set check-in time from pending record's checkin_time (new format: "2025-12-31 04:40 PM")
        if (record.checkin_time) {
          // Extract time portion from "2025-12-31 04:40 PM" -> "04:40 PM"
          const timeOnly = extractTimeFromTimestamp(record.checkin_time);
          if (!userTouchedCheckIn.current)
            setCheckInTime(parseTimeTo12Hour(timeOnly));
        } else {
          if (!userTouchedCheckIn.current)
            setCheckInTime({ hour: "", min: "", meridian: "AM" });
        }

        // Clear checkout time for pending record (user to input) — only if user hasn't touched it
        if (!userTouchedCheckOut.current)
          setCheckOutTime({ hour: "", min: "", meridian: "AM" });
      } else {
        // No pending record: show today's record or empty if none
        const todayISO = getISTDateString(); // "YYYY-MM-DD"
        const todayDisplay = formatToMMDDYYYY(todayISO); // "MM/DD/YYYY"
        setDisplayDate(todayDisplay);

        // Find today's record in formatted rows
        const latestEntry = formattedRows.find(
          (item) => item.attendance_date === todayDisplay,
        );

        if (latestEntry) {
          // Set check-in time (new format: "2025-12-31 04:40 PM")
          if (latestEntry.checkin_time) {
            const timeOnly = extractTimeFromTimestamp(latestEntry.checkin_time);
            if (!userTouchedCheckIn.current)
              setCheckInTime(parseTimeTo12Hour(timeOnly));
          } else {
            if (!userTouchedCheckIn.current)
              setCheckInTime({ hour: "", min: "", meridian: "AM" });
          }

          // Set check-out time and date (new format: "2026-01-01 01:00 AM")
          if (latestEntry.checkout_time) {
            const timeOnly = extractTimeFromTimestamp(
              latestEntry.checkout_time,
            );
            if (!userTouchedCheckOut.current)
              setCheckOutTime(parseTimeTo12Hour(timeOnly));

            // IMPORTANT: Extract the actual checkout date from the checkout_time
            const checkoutDateFromTime = extractDateFromTimestamp(
              latestEntry.checkout_time,
            );
            if (checkoutDateFromTime && checkoutDateFromTime !== "—") {
              // Convert MM/DD/YYYY back to YYYY-MM-DD for the date input
              const [month, day, year] = checkoutDateFromTime.split("/");
              const checkoutDateISO = `${year}-${month.padStart(
                2,
                "0",
              )}-${day.padStart(2, "0")}`;
              if (!userTouchedCheckOutDate.current)
                setCheckOutDate(checkoutDateISO);

              // Check if dates are different for highlighting
              const checkinDateFromTime = extractDateFromTimestamp(
                latestEntry.checkin_time,
              );
              if (checkinDateFromTime !== checkoutDateFromTime) {
                setDatesCrossMidnight(true);
              }
            } else {
              if (!userTouchedCheckOutDate.current) setCheckOutDate(todayISO);
            }
          } else {
            if (!userTouchedCheckOut.current)
              setCheckOutTime({ hour: "", min: "", meridian: "AM" });
            if (!userTouchedCheckOutDate.current) setCheckOutDate(todayISO);
          }
        } else {
          // No record for today
          if (!userTouchedCheckIn.current)
            setCheckInTime({ hour: "", min: "", meridian: "AM" });
          if (!userTouchedCheckOut.current)
            setCheckOutTime({ hour: "", min: "", meridian: "AM" });
          if (!userTouchedCheckOutDate.current) setCheckOutDate(todayISO);
        }
      }
    } catch (err) {
      console.error("Error fetching today history:", err);
    }
  };

  const formatDuration = (interval) => {
    if (!interval || typeof interval !== "object") return "-";

    // days field is set when the interval is exactly N*24h (e.g. 24h → { days:1, hours:0 })
    const totalH = (interval.days || 0) * 24 + (interval.hours || 0);
    const m = interval.minutes || 0;
    const s = interval.seconds || 0;

    // Handle negative durations (show as error)
    if (totalH < 0 || m < 0 || s < 0) {
      return "Invalid";
    }

    if (totalH === 0 && m === 0 && s === 0) return "0m";

    const parts = [];
    if (totalH > 0) parts.push(`${totalH}h`);
    if (m > 0) parts.push(`${m}m`);
    if (totalH === 0 && m === 0 && s > 0) parts.push(`${s}s`);

    return parts.join(" ");
  };

  // Helper function to extract date from timestamp
  const extractDateFromTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    try {
      const [datePart] = timestamp.split(" ");
      return formatDate(datePart);
    } catch (error) {
      return "—";
    }
  };

  // Helper function to extract time from timestamp
  const extractTimeFromTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    try {
      // Handle format like "2025-12-31 04:40 PM" -> extract "04:40 PM"
      const parts = timestamp.split(" ");
      if (parts.length >= 3) {
        return `${parts[1]} ${parts[2]}`; // "04:40 PM"
      } else if (parts.length === 2) {
        return parts[1]; // Just time part
      }
      return timestamp;
    } catch (error) {
      return timestamp;
    }
  };

  const fetchHistoryByDate = async (
    dateOrNull,
    empIdParam,
    userRoleParam,
    empNameParam = "",
    dateFromParam = null,
    dateToParam = null,
    pageParam = 1,
  ) => {
    try {
      // Use provided parameters or fall back to state
      const finalEmpId = empIdParam || empId;
      const finalUserRole = userRoleParam || userRole;
      const finalEmpName = empNameParam || searchEmpName;
      const finalPage = pageParam || page + 1;
      const finalDateFrom = dateFromParam || searchDateFrom;
      const finalDateTo = dateToParam || searchDateTo;
      const finalDate = dateOrNull || searchDate;

      console.log("fetchHistoryByDate called with:", {
        page: finalPage,
        limit,
        dateFrom: finalDateFrom,
        dateTo: finalDateTo,
        date: finalDate,
      });

      const queryParams = new URLSearchParams({
        page: finalPage,
        limit: limit,
        currentDate: getISTDateString(currentDateTime),
        boId: boId, // Add boId for proper filtering
      });

      // Add date or date range parameters
      if (finalDateFrom && finalDateTo) {
        queryParams.append("dateFrom", finalDateFrom);
        queryParams.append("dateTo", finalDateTo);
      } else if (finalDate) {
        queryParams.append("date", finalDate);
      }

      // Normalize roles
      const normalizeRoles = (roles) => {
        if (!roles) return [];
        return Array.isArray(roles)
          ? roles.map((r) => r.toLowerCase())
          : [roles.toLowerCase()];
      };

      const normalizedRoles = normalizeRoles(finalUserRole);
      const isAdmin =
        normalizedRoles.includes("admin") || normalizedRoles.includes("admins");
      const isHR = ["hr", "hrs", "human resource", "human resources"].some(
        (role) => normalizedRoles.includes(role),
      );

      if (!isAdmin && !isHR) {
        // For regular employees, always filter by their empId
        queryParams.append("empId", finalEmpId);
      } else if (finalEmpName) {
        // For admin/HR, allow searching by employee name
        queryParams.append("empName", finalEmpName);
      }

      // Add column filters to query params
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          // Map the display column names to backend field names
          const fieldMap = {
            "Attendance Date": "attendance_date",
            "Employee ID": "emp_id",
            "Employee Name": "employee_name",
            "Check-In Date": "checkin_date",
            "Check-In Time": "checkin_time",
            "Check-Out Date": "checkout_date",
            "Check-Out Time": "checkout_time",
            "Working Hours": "total_hours",
            "Check In Comments": "checkin_comments",
            "Check Out Comments": "checkout_comments",
          };
          const backendField = fieldMap[key] || key;
          queryParams.append(backendField, filters[key]);
        }
      });

      const res = await fetch(
        `${BASE_URL}/api/checkin/history?${queryParams.toString()}`,
      );
      const data = await res.json();

      console.log("API Response:", data.pagination);

      setHistoryData(data.data || []);
      const newTotalPages = data.pagination?.totalPages || 1;
      console.log("Setting historyTotalPages to:", newTotalPages);
      setHistoryTotalPages(newTotalPages);

      return data;
    } catch (err) {
      console.error("Error fetching history:", err);
      setHistoryData([]);
      setHistoryTotalPages(1);
      return null;
    }
  };

  // ── Excel export — Admin/HR only ────────────────────────────────────────────
  const handleExportAttendanceExcel = async () => {
    try {
      // Build params exactly the same way fetchHistoryByDate does,
      // but override page/limit to get all records at once.
      const normalizeRoles = (r) => {
        if (!r) return [];
        return Array.isArray(r)
          ? r.map((x) => x.toLowerCase())
          : [r.toLowerCase()];
      };
      const normalizedRoles = normalizeRoles(userRole);
      const isAdmin =
        normalizedRoles.includes("admin") || normalizedRoles.includes("admins");
      const isHR = ["hr", "hrs", "human resource", "human resources"].some(
        (r) => normalizedRoles.includes(r),
      );

      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        boId: boId,
        currentDate: getISTDateString(currentDateTime),
      });

      // Date / date-range filters (mirrors fetchHistoryByDate)
      if (searchDateFrom && searchDateTo) {
        queryParams.append("dateFrom", searchDateFrom);
        queryParams.append("dateTo", searchDateTo);
      } else if (searchDate) {
        queryParams.append("date", searchDate);
      }

      // Employee filter
      if (!isAdmin && !isHR) {
        queryParams.append("empId", empId);
      } else if (searchEmpName) {
        queryParams.append("empName", searchEmpName);
      }

      // Column-level filters (the popover filter values)
      const fieldMap = {
        "Attendance Date": "attendance_date",
        "Employee ID": "emp_id",
        "Employee Name": "employee_name",
        "Check-In Date": "checkin_date",
        "Check-In Time": "checkin_time",
        "Check-Out Date": "checkout_date",
        "Check-Out Time": "checkout_time",
        "Working Hours": "total_hours",
        "Check In Comments": "checkin_comments",
        "Check Out Comments": "checkout_comments",
      };
      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          queryParams.append(fieldMap[key] || key, filters[key]);
        }
      });

      const res = await fetch(
        `${BASE_URL}/api/checkin/history?${queryParams.toString()}`,
      );
      const data = await res.json();
      const rows = data?.data || [];

      if (rows.length === 0) {
        alert("No data to export.");
        return;
      }

      const excelRows = rows.map((row) => ({
        "Employee ID": row.emp_id || "",
        "Employee Name": row.employee_name || "",
        "Attendance Date": row.attendance_date || "",
        "Check-In Time": row.checkin_time
          ? extractTimeFromTimestamp(row.checkin_time)
          : "",
        "Check-Out Time": row.checkout_time
          ? extractTimeFromTimestamp(row.checkout_time)
          : "",
        "Working Hours": row.total_hours
          ? typeof row.total_hours === "object"
            ? `${row.total_hours.hours || 0}h ${row.total_hours.minutes || 0}m`
            : row.total_hours
          : "",
        "Check-In Comments": row.checkin_comments || "",
        "Check-Out Comments": row.checkout_comments || "",
      }));

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Attendance");

      const headers = Object.keys(excelRows[0]);

      worksheet.addTable({
        name: "AttendanceTable",
        ref: "A1",
        headerRow: true,
        totalsRow: false,
        style: {
          theme: "TableStyleMedium2",
          showRowStripes: true,
          showFirstColumn: false,
          showLastColumn: false,
        },
        columns: headers.map((header) => ({
          name: header,
        })),
        rows: excelRows.map((row) => headers.map((header) => row[header])),
      });

      // Auto column widths
      headers.forEach((header, index) => {
        const maxLength = Math.max(
          header.length,
          ...excelRows.map((row) => String(row[header] || "").length),
        );

        worksheet.getColumn(index + 1).width = maxLength + 2;
      });

      const dateStr =
        searchDateFrom && searchDateTo
          ? searchDateFrom === searchDateTo
            ? searchDateFrom
            : `${searchDateFrom}_to_${searchDateTo}`
          : searchDate || getISTDateString();

      const buffer = await workbook.xlsx.writeBuffer();

      saveAs(new Blob([buffer]), `Attendance_${dateStr}.xlsx`);
    } catch (err) {
      console.error("Excel export error:", err);
      alert("Failed to export. Please try again.");
    }
  };

  // Debounce the fetch to avoid firing on every keystroke
  const debouncedFetchHistory = debounce(
    (
      dateOrNull,
      empIdParam,
      userRoleParam,
      empNameParam,
      dateFromParam,
      dateToParam,
      pageParam,
    ) => {
      fetchHistoryByDate(
        dateOrNull,
        empIdParam,
        userRoleParam,
        empNameParam,
        dateFromParam,
        dateToParam,
        pageParam,
      );
    },
    500,
  ); // 500ms delay

  // Helper function to get weekly data for chart
  const getWeeklyChartData = () => {
    const today = getISTDateObj();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const data = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - dayOfWeek + i);
      // Format using local date parts to avoid UTC offset shifting the date
      const dateISO = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      // Match against weeklyHistory (attendance_date is YYYY-MM-DD from API)
      let hours = 0;
      if (
        weeklyHistory &&
        Array.isArray(weeklyHistory) &&
        weeklyHistory.length > 0
      ) {
        const record = weeklyHistory.find((r) => {
          if (!r || !r.attendance_date) return false;
          // attendance_date may be YYYY-MM-DD or MM/DD/YYYY
          const raw = r.attendance_date;
          if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw === dateISO;
          // MM/DD/YYYY → convert to YYYY-MM-DD for comparison
          const [mm, dd, yyyy] = raw.split("/");
          return (
            `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}` === dateISO
          );
        });

        if (record && record.total_hours) {
          hours =
            typeof record.total_hours === "object"
              ? (record.total_hours.days || 0) * 24 +
                (record.total_hours.hours || 0) +
                (record.total_hours.minutes || 0) / 60
              : parseFloat(record.total_hours) || 0;
        }
      }

      data.push({
        day: days[i],
        hours: Math.round(hours * 10) / 10,
        date: dateISO,
      });
    }
    return data;
  };

  // Fetch the full current week's attendance for the bar chart
  const fetchWeeklyHistory = async () => {
    if (!empId || !boId) return;
    try {
      const today = getISTDateObj();
      const dayOfWeek = today.getDay();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - dayOfWeek);
      const saturday = new Date(sunday);
      saturday.setDate(sunday.getDate() + 6);

      const fmt = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      const params = new URLSearchParams({
        page: 1,
        limit: 7,
        empId,
        boId,
        dateFrom: fmt(sunday),
        dateTo: fmt(saturday),
      });

      const res = await fetch(`${BASE_URL}/api/checkin/history?${params}`);
      const data = await res.json();
      setWeeklyHistory(data?.data || []);
    } catch (err) {
      console.error("Error fetching weekly history for chart:", err);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 1 }}>
      <ThemeProvider theme={FormThemes}>
        <CssBaseline />
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            backgroundColor: "#ffffff",
            overflow: "hidden",
            border: "1px solid #e3edf7",
            boxShadow: "0 2px 16px rgba(26,95,168,0.08)",
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1a5fa8 0%, #2979c8 100%)",
              px: 1.5,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="700"
              sx={{ color: "#fff", letterSpacing: 0.5, fontSize: "0.9rem" }}
            >
              Check-In / Check-Out
            </Typography>
            <Tooltip title="View Attendance History">
              <IconButton
                onClick={() => {
                  setPage(0);
                  setHistoryOpen(true);
                  fetchHistoryByDate(
                    null,
                    empId,
                    userRole,
                    searchEmpName,
                    searchDateFrom,
                    searchDateTo,
                    1,
                  );
                }}
                sx={{
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                  p: 0.4,
                }}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              p: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
              gap: 1.5,
            }}
          >
            {/* LEFT SIDEBAR: Profile, Chart & Today's Info */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Employee Profile */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Avatar
                  src={profilePicture || undefined}
                  sx={{
                    width: 70,
                    height: 70,
                    backgroundColor: "#1a5fa8",
                    fontSize: "1.8rem",
                  }}
                >
                  {!profilePicture &&
                    (() => {
                      const name = empName.employee_name || "";
                      const parts = name.split(" ");

                      if (parts.length >= 2) {
                        return (
                          parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
                        ).toUpperCase();
                      }

                      return name.charAt(0).toUpperCase() || "E";
                    })()}
                </Avatar>

                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: "#1a5fa8",
                      fontSize: "0.9rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {empName.emp_id}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {empName.employee_name}
                  </Typography>
                </Box>
              </Box>

              {/* Weekly Chart */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#1a5fa8",
                    fontSize: "0.75rem",
                    display: "block",
                    mb: 3,
                  }}
                >
                  Weekly Hours
                </Typography>

                {(() => {
                  const chartData = getWeeklyChartData();
                  const CHART_HEIGHT = 100; // px — container height
                  const maxHours = Math.max(
                    ...chartData.map((d) => d.hours),
                    1,
                  );

                  const getBarColor = (hours) => {
                    if (hours >= 9) return "#22c55e";
                    if (hours >= 7) return "#3b82f6";
                    if (hours >= 5) return "#f59e0b";
                    if (hours > 0) return "#ef4444";
                    return "#d1d5db";
                  };

                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-around",
                        height: CHART_HEIGHT,
                        gap: 0.6,
                      }}
                    >
                      {chartData.map((item, idx) => {
                        // Scale bar height proportionally so the tallest bar = 90% of container
                        const barHeight =
                          item.hours > 0
                            ? Math.max(
                                (item.hours / maxHours) * CHART_HEIGHT * 0.9,
                                5,
                              )
                            : 5;

                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                color: "#555",
                                mb: 0.3,
                              }}
                            >
                              {item.hours}h
                            </Typography>

                            <Box
                              sx={{
                                width: "100%",
                                height: `${barHeight}px`,
                                backgroundColor: getBarColor(item.hours),
                                borderRadius: "4px 4px 0 0",
                                transition: "all 0.3s ease",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",

                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  opacity: 0.9,
                                },
                              }}
                              title={`${item.hours}h`}
                            />

                            <Typography
                              variant="caption"
                              sx={{
                                mt: 0.5,
                                fontSize: "0.62rem",
                                fontWeight: 600,
                                color: "#666",
                              }}
                            >
                              {item.day}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}
              </Box>

              {/* Today's Info */}

              {/* Today's Info */}
              <Box
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1.5,
                  p: 1,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "1a5fa8",
                    fontSize: "0.7rem",
                    display: "block",
                  }}
                >
                  Today:{" "}
                  {currentDateTime.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                    timeZone: "Asia/Kolkata",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#666", fontSize: "0.8rem" }}
                >
                  {currentDateTime.toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: "Asia/Kolkata",
                  })}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT SIDE: Form */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Check-In Section */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.6fr 0.6fr 0.6fr",
                  gap: 0.5,
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  type="date"
                  label="Check-In Date"
                  value={(() => {
                    if (displayDate && displayDate.includes("/")) {
                      const [month, day, year] = displayDate.split("/");
                      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
                    }
                    return getISTDateString();
                  })()}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const formattedDate = formatToMMDDYYYY(selectedDate);
                    userIsEditing.current = true;
                    userTouchedCheckIn.current = true;
                    setDisplayDate(formattedDate);
                    setCheckOutDate(selectedDate);
                    setValidationError("");
                    setDatesCrossMidnight(false);
                  }}
                  disabled={isCheckedIn}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: getTwoDaysAgoDate(),
                    max: getTodayDate(),
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                />

                <TextField
                  select
                  label="Hr"
                  size="small"
                  value={checkInTime.hour}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    setCheckInTime({ ...checkInTime, hour: e.target.value });
                  }}
                  disabled={isCheckedIn}
                  fullWidth
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 120 } } },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <MenuItem value="">--</MenuItem>
                  {hours.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Min"
                  size="small"
                  value={checkInTime.min}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    setCheckInTime({ ...checkInTime, min: e.target.value });
                  }}
                  disabled={isCheckedIn}
                  fullWidth
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 120 } } },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <MenuItem value="">--</MenuItem>
                  {minutes.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="AM/PM"
                  size="small"
                  value={checkInTime.meridian}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    setCheckInTime({
                      ...checkInTime,
                      meridian: e.target.value,
                    });
                  }}
                  disabled={isCheckedIn}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  {ampm.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Check-Out Section */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.6fr 0.6fr 0.6fr",
                  gap: 0.5,
                  alignItems: "flex-end",
                }}
              >
                <TextField
                  type="date"
                  label="Check-Out Date"
                  value={checkOutDate}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    userTouchedCheckOutDate.current = true;
                    setCheckOutDate(e.target.value);
                    setValidationError("");
                    setDatesCrossMidnight(false);
                  }}
                  disabled={checkOutDisabled}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: getTwoDaysAgoDate(),
                    max: getTodayDate(),
                  }}
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                />

                <TextField
                  select
                  label="Hr"
                  size="small"
                  value={checkOutTime.hour}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    userTouchedCheckOut.current = true;
                    setCheckOutTime({ ...checkOutTime, hour: e.target.value });
                  }}
                  disabled={checkOutDisabled}
                  fullWidth
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 120 } } },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <MenuItem value="">--</MenuItem>
                  {hours.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Min"
                  size="small"
                  value={checkOutTime.min}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    userTouchedCheckOut.current = true;
                    setCheckOutTime({ ...checkOutTime, min: e.target.value });
                  }}
                  disabled={checkOutDisabled}
                  fullWidth
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: 120 } } },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  <MenuItem value="">--</MenuItem>
                  {minutes.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="AM/PM"
                  size="small"
                  value={checkOutTime.meridian}
                  onChange={(e) => {
                    userIsEditing.current = true;
                    userTouchedCheckOut.current = true;
                    setCheckOutTime({
                      ...checkOutTime,
                      meridian: e.target.value,
                    });
                  }}
                  disabled={checkOutDisabled}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      fontSize: "0.75rem",
                      py: 0.3,
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                >
                  {ampm.map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Comments */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#666",
                    fontSize: "0.65rem",
                    display: "block",
                    mb: 0.2,
                  }}
                >
                  Comments
                </Typography>
                <TextField
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  multiline
                  rows={1}
                  fullWidth
                  size="small"
                  placeholder="Comments (optional)"
                  sx={{
                    "& .MuiOutlinedInput-input": { fontSize: "0.75rem" },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f9f9f9",
                    },
                  }}
                />
              </Box>

              {/* Validation Error */}
              {validationError && (
                <Alert
                  severity="error"
                  sx={{ borderRadius: 1, py: 0.5, fontSize: "0.75rem" }}
                >
                  {validationError}
                </Alert>
              )}

              {/* Buttons */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.5,
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleCheckIn}
                  disabled={checkInDisabled}
                  sx={{
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    backgroundColor: "#1a5fa8",
                    "&:hover": {
                      backgroundColor: "#2979c8",
                    },
                    "&:disabled": {
                      backgroundColor: "#ccc",
                      color: "#999",
                    },
                  }}
                >
                  ✓ IN
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCheckOut}
                  disabled={checkOutDisabled}
                  sx={{
                    py: 0.5,
                    borderRadius: 1,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    backgroundColor: "#6c757d",
                    "&:hover": {
                      backgroundColor: "#5a6268",
                    },
                    "&:disabled": {
                      backgroundColor: "#ccc",
                      color: "#999",
                    },
                  }}
                >
                  OUT
                </Button>
              </Box>

              {statusMsg && (
                <Alert
                  severity={
                    statusMsg.includes("PENDING CHECKOUT") ? "warning" : "info"
                  }
                  sx={{
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    py: 0.5,
                    "& .MuiAlert-message": {
                      fontWeight: statusMsg.includes("PENDING CHECKOUT")
                        ? "600"
                        : "normal",
                      wordBreak: "break-word",
                    },
                  }}
                >
                  {statusMsg}
                </Alert>
              )}

              {datesCrossMidnight && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#4caf50",
                    fontStyle: "italic",
                    textAlign: "center",
                    fontSize: "0.7rem",
                  }}
                >
                  ✓ Midnight adjusted
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* History Dialog */}
        <Dialog
          open={historyOpen}
          onClose={handleHistoryClose}
          fullWidth
          maxWidth="xl"
          PaperProps={{
            sx: {
              height: "90vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 1,
              bgcolor: "#f5f5f5",
              borderBottom: "2px solid #1976d2",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 600, color: "#1976d2" }}
              >
                📊 Attendance History
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {/* Excel download — Admin/HR only */}
                {(() => {
                  const nr = Array.isArray(userRole)
                    ? userRole.map((r) => r.toLowerCase())
                    : [String(userRole || "").toLowerCase()];
                  const canExport =
                    nr.some((r) => r.includes("admin")) ||
                    nr.some((r) =>
                      [
                        "hr",
                        "hrs",
                        "human resource",
                        "human resources",
                      ].includes(r),
                    );
                  return canExport ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={handleExportAttendanceExcel}
                      sx={{
                        fontSize: "0.75rem",
                        height: 30,
                        textTransform: "none",
                      }}
                    >
                      ⬇ Download Excel
                    </Button>
                  ) : null;
                })()}
                <IconButton
                  aria-label="close"
                  onClick={handleHistoryClose}
                  sx={{ color: "grey.500" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 3,
              bgcolor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              height: "calc(90vh - 120px)",
              overflow: "hidden",
            }}
          >
            {/* Search Date Field */}
            <Box
              sx={{
                mb: 2,
                flexShrink: 0,
                bgcolor: "#f5f5f5",
                borderRadius: 1,
                p: 1.5,
                border: "1px solid #e0e0e0",
              }}
            >
              {/* Quick Filters Row */}
              <Box display="flex" gap={0.5} alignItems="center" mb={1}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#666",
                    mr: 0.5,
                    fontSize: "0.875rem",
                  }}
                >
                  Quick:
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    py: 0.25,
                    px: 1,
                    fontSize: "0.875rem",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    const today = getTodayDate();
                    setSearchDate(today);
                    setSearchDateFrom(today);
                    setSearchDateTo(today);
                    setPage(0);
                    // Call with proper parameters: dateOrNull, empId, userRole, empName, dateFrom, dateTo, page
                    fetchHistoryByDate(
                      null,
                      empId,
                      userRole,
                      searchEmpName,
                      today,
                      today,
                      1,
                    );
                  }}
                >
                  Today
                </Button>
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    py: 0.25,
                    px: 1,
                    fontSize: "0.875rem",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    const yesterday = getYesterdayDate();
                    setSearchDate(yesterday);
                    setSearchDateFrom(yesterday);
                    setSearchDateTo(yesterday);
                    setPage(0);
                    // Call with proper parameters: dateOrNull, empId, userRole, empName, dateFrom, dateTo, page
                    fetchHistoryByDate(
                      null,
                      empId,
                      userRole,
                      searchEmpName,
                      yesterday,
                      yesterday,
                      1,
                    );
                  }}
                >
                  Yesterday
                </Button>
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    py: 0.25,
                    px: 1,
                    fontSize: "0.875rem",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    const today = getISTDateObj();
                    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
                    const pad = (n) => String(n).padStart(2, "0");

                    // Week start: Sunday (subtract dayOfWeek days)
                    const weekStart = new Date(today);
                    weekStart.setDate(today.getDate() - dayOfWeek);
                    const from = `${weekStart.getFullYear()}-${pad(weekStart.getMonth() + 1)}-${pad(weekStart.getDate())}`;

                    // Week end: Saturday (add 6 - dayOfWeek days)
                    const weekEnd = new Date(today);
                    weekEnd.setDate(today.getDate() + (6 - dayOfWeek));
                    const to = `${weekEnd.getFullYear()}-${pad(weekEnd.getMonth() + 1)}-${pad(weekEnd.getDate())}`;

                    setSearchDateFrom(from);
                    setSearchDateTo(to);
                    setSearchDate(from);
                    setPage(0);
                    fetchHistoryByDate(
                      null,
                      empId,
                      userRole,
                      searchEmpName,
                      from,
                      to,
                      1,
                    );
                  }}
                >
                  This Week
                </Button>
                <Button
                  size="small"
                  variant="text"
                  sx={{
                    py: 0.25,
                    px: 1,
                    fontSize: "0.875rem",
                    minWidth: "auto",
                    textTransform: "none",
                  }}
                  onClick={() => {
                    const today = getISTDateObj();
                    const pad = (n) => String(n).padStart(2, "0");
                    const from = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`;
                    const to = getTodayDate();
                    setSearchDateFrom(from);
                    setSearchDateTo(to);
                    setSearchDate(to);
                    setPage(0);
                    fetchHistoryByDate(
                      null,
                      empId,
                      userRole,
                      searchEmpName,
                      from,
                      to,
                      1,
                    );
                  }}
                >
                  This Month
                </Button>

                {/* Date Range Info - Inline */}
                {searchDateFrom &&
                  searchDateTo &&
                  searchDateFrom !== searchDateTo && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontStyle: "italic", fontSize: "0.875rem", ml: 2 }}
                    >
                      📅 {searchDateFrom} to {searchDateTo}
                    </Typography>
                  )}
              </Box>

              {/* Date Range, Employee Search, and Search Button Row */}
              <Box display="flex" gap={1.5} alignItems="center">
                <TextField
                  type="date"
                  value={searchDateFrom || searchDate}
                  onChange={(e) => {
                    const newDateFrom = e.target.value;
                    setSearchDateFrom(newDateFrom);
                    setSearchDate(newDateFrom);
                    setPage(0);
                    // Auto-fetch on date change - use range if both dates exist
                    if (searchDateTo) {
                      debouncedFetchHistory(
                        null,
                        empId,
                        userRole,
                        searchEmpName,
                        newDateFrom,
                        searchDateTo,
                        1,
                      );
                    } else {
                      debouncedFetchHistory(
                        null,
                        empId,
                        userRole,
                        searchEmpName,
                        newDateFrom,
                        null,
                        1,
                      );
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  placeholder="From"
                  sx={{
                    width: 150,
                    "& .MuiInputBase-root": { height: 32, bgcolor: "white" },
                    "& .MuiInputBase-input": { py: 0.5, fontSize: "0.875rem" },
                  }}
                />
                <Typography variant="caption" sx={{ color: "#999" }}>
                  to
                </Typography>
                <TextField
                  type="date"
                  value={searchDateTo || searchDate}
                  onChange={(e) => {
                    const newDateTo = e.target.value;
                    setSearchDateTo(newDateTo);
                    setPage(0);
                    // Auto-fetch on date change - use range if both dates exist
                    if (searchDateFrom) {
                      debouncedFetchHistory(
                        null,
                        empId,
                        userRole,
                        searchEmpName,
                        searchDateFrom,
                        newDateTo,
                        1,
                      );
                    } else {
                      debouncedFetchHistory(
                        null,
                        empId,
                        userRole,
                        searchEmpName,
                        null,
                        newDateTo,
                        1,
                      );
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  placeholder="To"
                  sx={{
                    width: 150,
                    "& .MuiInputBase-root": { height: 32, bgcolor: "white" },
                    "& .MuiInputBase-input": { py: 0.5, fontSize: "0.875rem" },
                  }}
                />

                {(() => {
                  const normalizeRoles = (roles) => {
                    if (!roles) return [];
                    return Array.isArray(roles)
                      ? roles.map((r) => r.toLowerCase())
                      : [roles.toLowerCase()];
                  };
                  const normalizedRoles = normalizeRoles(userRole);
                  const isAdmin =
                    normalizedRoles.includes("admin") ||
                    normalizedRoles.includes("admins");
                  const isHR = [
                    "hr",
                    "hrs",
                    "human resource",
                    "human resources",
                  ].some((role) => normalizedRoles.includes(role));
                  return isAdmin || isHR ? (
                    <TextField
                      type="text"
                      value={searchEmpName}
                      size="small"
                      onChange={(e) => {
                        const newEmpName = e.target.value;
                        setSearchEmpName(newEmpName);
                        setPage(0);
                        // Call with proper parameters: dateOrNull, empId, userRole, empName, dateFrom, dateTo, page
                        debouncedFetchHistory(
                          null,
                          empId,
                          userRole,
                          newEmpName,
                          searchDateFrom,
                          searchDateTo,
                          1,
                        );
                      }}
                      placeholder="Search employee..."
                      sx={{
                        width: 200,
                        "& .MuiInputBase-root": {
                          height: 32,
                          bgcolor: "white",
                        },
                        "& .MuiInputBase-input": {
                          py: 0.5,
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  ) : null;
                })()}
              </Box>
            </Box>

            {/* Attendance Table */}
            <ThemeProvider theme={Table_theme}>
              <CssBaseline />
              <TableContainer
                component={Paper}
                sx={{
                  flex: 1,
                  overflow: "auto",
                  minHeight: 0,
                }}
              >
                <Table stickyHeader sx={{ minWidth: 1400 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { name: "Attendance Date", filterable: true },
                        { name: "Employee ID", filterable: true },
                        { name: "Employee Name", filterable: true },
                        { name: "Check-In Date", filterable: false },
                        { name: "Check-In Time", filterable: false },
                        { name: "Check-Out Date", filterable: false },
                        { name: "Check-Out Time", filterable: false },
                        { name: "Working Hours", filterable: false },
                        { name: "Check In Comments", filterable: false },
                        { name: "Check Out Comments", filterable: false },
                      ].map((column, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {column.filterable ? (
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{ gap: 0.5 }}
                            >
                              <strong>{column.name}</strong>
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleFilterIconClick(column.name, e)
                                }
                              >
                                <FilterList fontSize="small" />
                              </IconButton>
                              <Popover
                                open={Boolean(anchorEls[column.name])}
                                anchorEl={anchorEls[column.name]}
                                onClose={() => handleFilterClose(column.name)}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                              >
                                <Box
                                  p={2}
                                  display="flex"
                                  flexDirection="column"
                                  gap={1}
                                  width={200}
                                >
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={`Filter ${column.name}`}
                                    value={filters[column.name] || ""}
                                    onChange={(e) =>
                                      setFilters((prev) => ({
                                        ...prev,
                                        [column.name]: e.target.value,
                                      }))
                                    }
                                  />
                                  <Box
                                    display="flex"
                                    justifyContent="space-between"
                                  >
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() =>
                                        handleFilterClose(column.name)
                                      }
                                      sx={{ minWidth: 70 }}
                                    >
                                      Apply
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        setFilters((prev) => ({
                                          ...prev,
                                          [column.name]: "",
                                        }));
                                        handleFilterClose(column.name);
                                      }}
                                      sx={{ minWidth: 70 }}
                                    >
                                      Clear
                                    </Button>
                                  </Box>
                                </Box>
                              </Popover>
                            </Box>
                          ) : (
                            <strong>{column.name}</strong>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {historyData.length > 0 ? (
                      historyData.map((row, idx) => {
                        const hasCheckin =
                          row.checkin_time && row.checkin_time !== "—";
                        const hasCheckout =
                          row.checkout_time && row.checkout_time !== "—";
                        // total_hours can be an interval object {hours, minutes} or a numeric string
                        const totalHoursNum =
                          typeof row.total_hours === "object" &&
                          row.total_hours !== null
                            ? (row.total_hours.days || 0) * 24 +
                              (row.total_hours.hours || 0) +
                              (row.total_hours.minutes || 0) / 60
                            : parseFloat(row.total_hours) || 0;
                        const isLowHours =
                          totalHoursNum < 8 && hasCheckin && hasCheckout;
                        const isIncomplete = hasCheckin && !hasCheckout;

                        return (
                          <TableRow
                            key={idx}
                            sx={{
                              borderLeft: isIncomplete
                                ? "4px solid #ff9800"
                                : isLowHours
                                  ? "4px solid #f44336"
                                  : "4px solid transparent",
                            }}
                          >
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {formatDate(row.attendance_date)}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {row.emp_id}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {row.employee_name}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {extractDateFromTimestamp(row.checkin_time)}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {extractTimeFromTimestamp(row.checkin_time)}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {extractDateFromTimestamp(row.checkout_time)}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              {extractTimeFromTimestamp(row.checkout_time)}
                            </TableCell>
                            <TableCell
                              sx={{
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                fontWeight: 600,
                                color: isLowHours
                                  ? "#d32f2f"
                                  : totalHoursNum >= 8
                                    ? "#2e7d32"
                                    : "text.primary",
                              }}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={1}
                              >
                                {formatDuration(row.total_hours)}
                                {isIncomplete && (
                                  <Chip
                                    label="Incomplete"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.65rem",
                                      bgcolor: "#ff9800",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{ textAlign: "center", maxWidth: 200 }}
                            >
                              {row.checkin_comments ? (
                                <Tooltip
                                  title={row.checkin_comments}
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {row.checkin_comments}
                                  </Box>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ textAlign: "center", maxWidth: 200 }}
                            >
                              {row.checkout_comments ? (
                                <Tooltip
                                  title={row.checkout_comments}
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {row.checkout_comments}
                                  </Box>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                          <Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                              📭 No records found
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Try adjusting your search criteria
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </ThemeProvider>
            {/* Legend and Pagination */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              flexWrap="wrap"
              gap={2}
              sx={{ flexShrink: 0 }}
            >
              {/* Legend */}
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: "#ff9800",
                      borderRadius: 0.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    Incomplete
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: "#f44336",
                      borderRadius: 0.5,
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    Low Hours
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} ml={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Per page:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(0); // Reset to first page
                    }}
                    sx={{
                      minWidth: 60,
                      "& .MuiOutlinedInput-root": {
                        height: "28px",
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </TextField>
                </Box>
              </Box>

              {/* Pagination */}
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={historyTotalPages || 1}
                  page={page + 1}
                  onChange={(_, newPage) => setPage(newPage - 1)}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: "8px",
                      minWidth: "32px",
                      height: "32px",
                      fontSize: "14px",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#b3dbea !important",
                      color: "black",
                      fontWeight: "bold",
                    },
                    "& .MuiPaginationItem-ellipsis": {
                      display: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>

          {/* <DialogActions sx={{ px: 3, py: 2, bgcolor: "#f5f5f5" }}>
            <Button onClick={handleHistoryClose} variant="outlined" sx={{ minWidth: 100 }}>
              Close
            </Button>
          </DialogActions> */}
        </Dialog>

        <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={validationError ? "error" : "info"}
            onClose={() => setOpen(false)}
            sx={{ width: "100%", borderRadius: 2 }}
          >
            {message || validationError || "No message available"}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </Container>
  );
};

export default EmployeeAttendance;
